import axios from "axios";
import { ENV_REACT_APP } from "../Configurations/ENV_Config";
import { getToken } from "../Utils/tokenStorage";

// Homeowner maintenance schedule (October 2026, Task 4). Same axios-direct +
// Bearer pattern as contractApi / permitApi. Owner-only endpoints under
// /api/property/:propertyId/maintenance.
const apiBase = (path: string) =>
  `${ENV_REACT_APP?.REACT_APP_API_BASE_URL || ""}${path}`;

const authHeaders = (): Record<string, string> => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export type MaintenanceCategory =
  | "hvac"
  | "plumbing"
  | "electrical"
  | "roofing"
  | "exterior"
  | "interior"
  | "appliances"
  | "safety"
  | "landscaping"
  | "pest"
  | "other";

export const MAINTENANCE_CATEGORY_LABELS: Record<MaintenanceCategory, string> = {
  hvac: "Heating & cooling",
  plumbing: "Plumbing",
  electrical: "Electrical",
  roofing: "Roofing",
  exterior: "Exterior",
  interior: "Interior",
  appliances: "Appliances",
  safety: "Safety",
  landscaping: "Landscaping",
  pest: "Pest control",
  other: "Other",
};

export const MAINTENANCE_CATEGORIES = Object.keys(
  MAINTENANCE_CATEGORY_LABELS
) as MaintenanceCategory[];

// Repeat options offered in the add/edit form (months; null = one-off).
export const REPEAT_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: "Doesn't repeat" },
  { value: 1, label: "Every month" },
  { value: 3, label: "Every 3 months" },
  { value: 6, label: "Every 6 months" },
  { value: 12, label: "Every year" },
  { value: 24, label: "Every 2 years" },
  { value: 60, label: "Every 5 years" },
];

export const repeatLabel = (months: number | null): string => {
  if (!months) return "One-time";
  const known = REPEAT_OPTIONS.find((o) => o.value === months);
  if (known) return known.label;
  return months % 12 === 0 ? `Every ${months / 12} years` : `Every ${months} months`;
};

export interface MaintenanceItem {
  _id: string;
  title: string;
  notes: string;
  category: MaintenanceCategory;
  source: "owner" | "recommended" | "simplerhome";
  recommendedKey: string | null;
  dueDate: string | null; // ISO, noon UTC — read with calendarDate helpers
  intervalMonths: number | null;
  status: "active" | "completed" | "dismissed";
  lastCompletedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface MaintenanceRecommendation {
  key: string;
  title: string;
  category: MaintenanceCategory;
  intervalMonths: number;
  description: string;
  trade: string | null;
}

export interface DismissedRecommendation {
  _id: string;
  recommendedKey: string;
  title: string;
}

export interface MaintenanceSchedule {
  items: MaintenanceItem[];
  recommendations: MaintenanceRecommendation[];
  dismissed: DismissedRecommendation[];
}

export interface MaintenanceInput {
  title?: string;
  notes?: string;
  category?: MaintenanceCategory;
  dueDate?: string; // YYYY-MM-DD
  intervalMonths?: number | null;
  recommendedKey?: string;
}

const base = (propertyId: string) =>
  apiBase(`/api/property/${encodeURIComponent(propertyId)}/maintenance`);

export const getMaintenance = async (propertyId: string): Promise<MaintenanceSchedule> => {
  const res = await axios.get(base(propertyId), { headers: authHeaders() });
  const d = res.data?.data || {};
  return {
    items: d.items || [],
    recommendations: d.recommendations || [],
    dismissed: d.dismissed || [],
  };
};

export const createMaintenance = async (
  propertyId: string,
  input: MaintenanceInput
): Promise<MaintenanceItem> => {
  const res = await axios.post(base(propertyId), input, { headers: authHeaders() });
  return res.data.data.item as MaintenanceItem;
};

export const updateMaintenance = async (
  propertyId: string,
  itemId: string,
  input: MaintenanceInput
): Promise<MaintenanceItem> => {
  const res = await axios.patch(`${base(propertyId)}/${itemId}`, input, {
    headers: authHeaders(),
  });
  return res.data.data.item as MaintenanceItem;
};

export const completeMaintenance = async (
  propertyId: string,
  itemId: string,
  completedAt?: string
): Promise<MaintenanceItem> => {
  const res = await axios.post(
    `${base(propertyId)}/${itemId}/complete`,
    completedAt ? { completedAt } : {},
    { headers: authHeaders() }
  );
  return res.data.data.item as MaintenanceItem;
};

export const deleteMaintenance = async (propertyId: string, itemId: string): Promise<void> => {
  await axios.delete(`${base(propertyId)}/${itemId}`, { headers: authHeaders() });
};

export const dismissRecommendation = async (
  propertyId: string,
  key: string
): Promise<DismissedRecommendation> => {
  const res = await axios.post(
    `${base(propertyId)}/recommendations/${encodeURIComponent(key)}/dismiss`,
    {},
    { headers: authHeaders() }
  );
  return res.data.data.dismissed as DismissedRecommendation;
};

export const maintenanceErrMsg = (e: any, fallback = "Something went wrong."): string =>
  e?.response?.data?.errors?.message || e?.response?.data?.message || e?.message || fallback;
